import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Upload } from 'lucide-react';
import {
  type CatalogLightingDetails,
  type CatalogLightingListItem,
  type CreateCatalogLightingRequest,
  type ListCatalogLightingQuery,
} from '@kreart/shared';
import { Button } from '@/components/ui/button';
import { CatalogTable, type ColumnDef } from './CatalogTable';
import { XlsxDropzone } from './XlsxDropzone';
import { LightingEditDrawer } from './LightingEditDrawer';
import { PriceImpactDialog } from './PriceImpactDialog';
import { lightingApi } from './api';
import { CatalogTabs } from '../CatalogTabs';

const PAGE_SIZE = 25;

export function LightingAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<CatalogLightingDetails | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [impactTarget, setImpactTarget] = useState<{
    kind: string;
    id: string;
    label: string;
  } | null>(null);

  const query: ListCatalogLightingQuery = {
    page,
    pageSize: PAGE_SIZE,
    search: search || undefined,
    includeDeleted: false,
  };

  const list = useQuery({
    queryKey: ['admin-lighting', query],
    queryFn: () => lightingApi.list(query),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-lighting'], exact: false });

  const createMut = useMutation({
    mutationFn: (body: CreateCatalogLightingRequest) => lightingApi.create(body),
    onSuccess: () => invalidate(),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: CreateCatalogLightingRequest }) =>
      lightingApi.update(id, body),
    onSuccess: () => invalidate(),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => lightingApi.remove(id),
    onSuccess: () => invalidate(),
  });

  const columns: ColumnDef<CatalogLightingListItem>[] = [
    {
      key: 'name',
      label: t('common.name', { defaultValue: 'Name' }),
      render: (l) => <span className="font-medium">{l.name}</span>,
    },
    { key: 'internalCode', label: 'Cod Intern', render: (l) => l.internalCode ?? '—' },
    { key: 'sku', label: 'Cod Produs', render: (l) => l.sku ?? '—' },
    {
      key: 'manufacturer',
      label: t('common.manufacturer', { defaultValue: 'Manufacturer' }),
      render: (l) => l.manufacturer ?? '—',
    },
    {
      key: 'pricePerPiece',
      label: t('lightingAdmin.pricePerPiece', { defaultValue: 'Price / piece' }),
      render: (l) => (l.pricePerPiece ? `${l.pricePerPiece} ${l.currency}` : '—'),
    },
    {
      key: 'active',
      label: t('common.active', { defaultValue: 'Active' }),
      render: (l) => (l.active ? '✓' : '—'),
    },
  ];

  const onAdd = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const onEdit = async (row: CatalogLightingListItem) => {
    const d = await lightingApi.get(row.id);
    setEditing(d);
    setDrawerOpen(true);
  };
  const onDelete = async (row: CatalogLightingListItem) => {
    if (!window.confirm(t('common.confirmDelete', { defaultValue: 'Delete?' }))) return;
    await deleteMut.mutateAsync(row.id);
  };
  const onSubmit = async (body: CreateCatalogLightingRequest) => {
    if (editing) {
      const beforePrice = editing.pricePerPiece ?? null;
      const updated = await updateMut.mutateAsync({ id: editing.id, body });
      setDrawerOpen(false);
      const afterPrice = updated.pricePerPiece ?? null;
      if (beforePrice !== null && afterPrice !== null && beforePrice !== afterPrice) {
        setImpactTarget({
          kind: 'lighting',
          id: editing.id,
          label: t('priceImpact.lightingLabel', {
            defaultValue: 'Lighting: {{name}} ({{before}} → {{after}})',
            name: updated.name,
            before: beforePrice,
            after: afterPrice,
          }),
        });
      }
    } else {
      await createMut.mutateAsync(body);
      setDrawerOpen(false);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold">
          {t('lightingAdmin.title', { defaultValue: 'Lighting (Iluminat)' })}
        </h1>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant="outline"
            onClick={() => setImportOpen(true)}
            data-testid="lighting-table-import"
          >
            <Upload className="mr-1.5 h-4 w-4" />
            {t('catalogAdmin.import', { defaultValue: 'Import' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => void lightingApi.exportXlsx()}
            data-testid="lighting-table-export"
          >
            <Download className="mr-1.5 h-4 w-4" />
            {t('catalogAdmin.export', { defaultValue: 'Export' })}
          </Button>
          <Button type="button" onClick={onAdd} data-testid="lighting-table-add">
            <Plus className="mr-1.5 h-4 w-4" />
            {t('lightingAdmin.create', { defaultValue: 'New lighting' })}
          </Button>
        </div>
      </div>
      <CatalogTabs />
      <CatalogTable<CatalogLightingListItem>
        columns={columns}
        data={list.data?.items ?? []}
        total={list.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        searchValue={search}
        onSearchChange={(v) => {
          setPage(1);
          setSearch(v);
        }}
        onRowEdit={onEdit}
        onRowDelete={onDelete}
        loading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        testId="lighting-table"
      />
      <LightingEditDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        initial={editing}
        onSubmit={onSubmit}
      />
      <XlsxDropzone
        open={importOpen}
        onClose={() => setImportOpen(false)}
        entityLabel={t('lightingAdmin.title', { defaultValue: 'Lighting' })}
        onPreview={async (f) => (await lightingApi.importDryRun(f)).plan}
        onConfirm={async (f) => {
          await lightingApi.importApply(f);
          await invalidate();
        }}
      />
      <PriceImpactDialog
        target={impactTarget}
        onClose={() => setImpactTarget(null)}
        fetchImpact={(id) => lightingApi.getImpact(id)}
        recompute={(id) => lightingApi.recomputeImpact(id)}
      />
    </div>
  );
}
